/**
 * F17.2.16 — Export PDF della scheda tecnica controsoffitto.
 *
 * Specchio strutturale di `exportTechnicalSheetPDF.ts` (wall) con queste
 * differenze ceiling-specific:
 *
 *  - Header: spessore = "pacchetto sospeso" anziché "spessore parete"
 *  - Composizione struttura: drop pendinatura, hangerSpacing primario/perpendicolare,
 *    primarySpacing/secondarySpacing, densità pendini, perimeter override
 *  - Voce di capitolato controsoffitto (testo generato auto-mirror della scheda HTML)
 *  - Norme: include UNI EN 13964 (controsoffitti) accanto a UNI 11424
 *  - Categoria badge nel layer table: "Orditura primaria" / "Orditura secondaria"
 *    derivati dall'ordine dei frame layers (stessa logica del HTML sheet)
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { LayerV2 } from '../../types';
import type { CeilingDimensionalState } from '../../hooks/useCeilingConfiguratorState';
import type { CeilingSectionModel } from '../section-view/ceilingModel';
import { rasterizeSvg } from './exportTechnicalSheetPDF';
import { formatNumber } from '@/utils/format';

interface ExportCeilingPdfOptions {
  layers: LayerV2[];
  ceiling: CeilingDimensionalState;
  model: CeilingSectionModel;
  /** Titolo descrittivo es. "Controsoffitto pendinato orditura doppia". */
  ceilingTitle?: string | null;
  /** Codice sistema, fallback "CS-CUSTOM". */
  systemCode?: string;
  stratigraphyName: string;
  stratigraphyDescription?: string;
  totalCostPerSqm: number;
  /** Elemento SVG della sezione tecnica (TechnicalCeilingDrawing). */
  sectionSvgElement?: SVGSVGElement | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  board: 'Lastra',
  ceiling_tile: 'Pannello',
  structure_frame: 'Orditura',
  structure_guide: 'Guida',
  insulation: 'Isolante',
  screw: 'Vite',
  finish: 'Finitura',
  accessory: 'Accessorio',
};

export async function exportCeilingTechnicalSheetPDF(opts: ExportCeilingPdfOptions): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  let cursorY = margin;

  const { ceiling, model } = opts;
  const isSuspended = ceiling.attachmentType === 'suspended';
  const totalThickness = model.totalDepthMm;

  // ===== Aggregati prestazioni =====
  const totalWeight = opts.layers.reduce((acc, l) => {
    const w = Number(l.material?.weight_per_sqm ?? 0);
    return acc + (Number.isFinite(w) ? w : 0);
  }, 0);
  const lambdaLayers = opts.layers.filter(l => Number(l.material?.thermal_conductivity ?? 0) > 0);
  const avgLambda = lambdaLayers.length > 0
    ? lambdaLayers.reduce((acc, l) => {
        const lam = Number(l.material?.thermal_conductivity ?? 0);
        const t = Number(l.thickness ?? l.material?.thickness ?? 0);
        return acc + lam * t;
      }, 0) / lambdaLayers.reduce((acc, l) => acc + Number(l.thickness ?? l.material?.thickness ?? 0), 0)
    : null;
  const bestRw = opts.layers.reduce((acc, l) => {
    const r = Number(l.material?.acoustic_performance ?? 0);
    return r > acc ? r : acc;
  }, 0);
  const fireClasses = Array.from(new Set(
    opts.layers
      .map(l => l.material?.fire_class)
      .filter((c): c is string => typeof c === 'string' && c.trim() !== ''),
  ));

  // ===== Frame level (primary/secondary via ordine) =====
  let frameCount = 0;
  const levelByLayerId = new Map<string, 'primary' | 'secondary'>();
  opts.layers.forEach((l) => {
    if (l.material?.category === 'structure_frame') {
      frameCount += 1;
      levelByLayerId.set(l.id, frameCount === 1 ? 'primary' : 'secondary');
    }
  });
  const primaryFrames = opts.layers.filter(l => levelByLayerId.get(l.id) === 'primary');
  const secondaryFrames = opts.layers.filter(l => levelByLayerId.get(l.id) === 'secondary');
  const boards = opts.layers.filter(l => l.material?.category === 'board' || l.material?.category === 'ceiling_tile');
  const insulations = opts.layers.filter(l => l.material?.category === 'insulation');
  const screwsLayers = opts.layers.filter(l => l.screwMaterial && (l.screwQuantity ?? 0) > 0);

  // ===== HEADER =====
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setTextColor(255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('SCHEDA TECNICA · DryConfig · Controsoffitto', margin, 10);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(opts.systemCode ?? 'CS-CUSTOM', margin, 18);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(opts.ceilingTitle ?? 'Controsoffitto a secco', margin, 24);

  // Box spessore a destra
  doc.setFontSize(8);
  doc.text('PACCHETTO SOSPESO', pageW - margin - 42, 10);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(`${totalThickness} mm`, pageW - margin, 22, { align: 'right' });

  cursorY = 34;
  doc.setTextColor(15, 23, 42);

  // ===== NOME STRATIGRAFIA =====
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(opts.stratigraphyName || 'Controsoffitto personalizzato', margin, cursorY);
  cursorY += 5;
  if (opts.stratigraphyDescription) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(opts.stratigraphyDescription, margin, cursorY);
    cursorY += 5;
    doc.setTextColor(15, 23, 42);
  }
  cursorY += 2;

  // ===== SEZIONE RASTERIZZATA =====
  if (opts.sectionSvgElement && opts.sectionSvgElement instanceof SVGSVGElement) {
    const dataUrl = await rasterizeSvg(opts.sectionSvgElement, 2);
    if (dataUrl) {
      const svg = opts.sectionSvgElement;
      const vb = svg.viewBox.baseVal;
      const ratio = vb.height / Math.max(1, vb.width);
      const imgW = pageW - margin * 2;
      const imgH = imgW * ratio;
      const finalH = Math.min(imgH, 80);
      const finalW = finalH / ratio;
      const cx = (pageW - finalW) / 2;
      doc.addImage(dataUrl, 'PNG', cx, cursorY, finalW, finalH, undefined, 'FAST');
      cursorY += finalH + 6;
    }
  }

  // ===== PRESTAZIONI (4 box) =====
  const perfData = [
    ['Resistenza al fuoco', fireClasses.length ? fireClasses.join(' / ') : '—', 'EI'],
    ['Isolamento acustico', bestRw > 0 ? `${Math.round(bestRw)}` : '—', 'Rw dB'],
    ['Cond. termica', avgLambda != null ? formatNumber(avgLambda, 3) : '—', 'λ W/mK'],
    ['Peso specifico', totalWeight > 0 ? formatNumber(totalWeight, 1) : '—', 'kg/m²'],
  ];
  const perfBoxW = (pageW - margin * 2) / 4;
  doc.setDrawColor(226, 232, 240);
  perfData.forEach((row, i) => {
    const x = margin + perfBoxW * i;
    doc.rect(x, cursorY, perfBoxW, 18);
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text(row[0], x + perfBoxW / 2, cursorY + 5, { align: 'center' });
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.text(String(row[1]), x + perfBoxW / 2, cursorY + 12, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(row[2], x + perfBoxW / 2, cursorY + 16, { align: 'center' });
  });
  cursorY += 22;
  doc.setTextColor(15, 23, 42);

  // ===== TABELLA STRATIGRAFIA (solaio → vista) =====
  const tableBody = opts.layers.map((l, idx) => {
    const m = l.material;
    const t = l.thickness ?? m?.thickness ?? 0;
    const level = levelByLayerId.get(l.id);
    let categoryLabel = CATEGORY_LABEL[m?.category ?? ''] ?? m?.category ?? '—';
    if (level === 'primary') categoryLabel = 'Orditura primaria';
    if (level === 'secondary') categoryLabel = 'Orditura secondaria';
    return [
      String(idx + 1),
      m?.name ?? '—',
      categoryLabel,
      t > 0 ? `${formatNumber(Number(t), 1)} mm` : '—',
      m?.weight_per_sqm ? `${formatNumber(Number(m.weight_per_sqm), 2)} kg/m²` : '—',
      m?.supplier ?? '—',
    ];
  });
  autoTable(doc, {
    startY: cursorY,
    head: [['#', 'Materiale', 'Categoria', 'Spessore', 'Peso', 'Fornitore']],
    body: tableBody,
    theme: 'grid',
    headStyles: { fillColor: [243, 244, 246], textColor: [55, 65, 81], fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      3: { halign: 'right' },
      4: { halign: 'right' },
    },
    margin: { left: margin, right: margin },
    foot: [[
      '', 'Totale pacchetto sospeso', '',
      `${totalThickness} mm`,
      `${formatNumber(totalWeight, 2)} kg/m²`,
      '',
    ]],
    footStyles: { fillColor: [249, 250, 251], textColor: [15, 23, 42], fontStyle: 'bold', fontSize: 8 },
  });
  cursorY = (doc as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? cursorY;
  cursorY += 6;

  // ===== COMPOSIZIONE STRUTTURA CEILING-SPECIFIC =====
  if (cursorY > pageH - 60) { doc.addPage(); cursorY = margin; }
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Composizione struttura', margin, cursorY);
  cursorY += 4;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const specs: string[] = [
    `Tipologia: Controsoffitto a secco`,
    `Sospensione: ${isSuspended ? 'Pendinato' : 'Aderente al solaio'}`,
  ];
  if (isSuspended) {
    specs.push(`Camera tecnica (drop): ${ceiling.ceilingDropMm} mm`);
    specs.push(`Passo pendini: ${ceiling.hangerSpacingPrimaryMm}×${ceiling.hangerSpacingPerpendicularMm} mm`);
    if (model.hangerCountPerSqm !== null) {
      specs.push(`Densità pendini: ${formatNumber(model.hangerCountPerSqm, 2)} pz/m²`);
    }
  }
  specs.push(`Orditura: ${model.hasDoubleStructure ? 'Doppia (primaria + secondaria)' : 'Singola'}`);
  specs.push(`Passo orditura primaria: ${ceiling.primarySpacingMm} mm`);
  if (model.hasDoubleStructure) {
    specs.push(`Passo orditura secondaria: ${ceiling.secondarySpacingMm ?? 500} mm`);
  }
  specs.push(`N. lastre: ${boards.length}  ·  N. primarie: ${primaryFrames.length}  ·  N. secondarie: ${secondaryFrames.length}  ·  N. isolanti: ${insulations.length}`);
  if (ceiling.perimeterProfileMlPerSqm !== null && ceiling.perimeterProfileMlPerSqm > 0) {
    specs.push(`Perimetro: ${formatNumber(ceiling.perimeterProfileMlPerSqm, 2)} ml/m² (override manuale)`);
  }
  if (screwsLayers.length > 0) {
    const screwsTotal = screwsLayers.reduce((acc, l) => acc + (l.screwQuantity ?? 0), 0);
    specs.push(`Viti per fissaggio: ${screwsLayers.length} layer · ${formatNumber(screwsTotal, 0)} pz/m²`);
  }
  specs.push(`Costo totale al m²: € ${formatNumber(opts.totalCostPerSqm, 2)}/m² (IVA esclusa, esclude profilo perimetrale per stanza)`);

  specs.forEach(s => {
    if (cursorY > pageH - 15) { doc.addPage(); cursorY = margin; }
    doc.text(`· ${s}`, margin, cursorY);
    cursorY += 4;
  });
  cursorY += 4;

  // ===== VOCE DI CAPITOLATO =====
  if (cursorY > pageH - 60) { doc.addPage(); cursorY = margin; }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Voce di capitolato', margin, cursorY);
  cursorY += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  // Genera testo capitolato (mirror del CeilingTechnicalSheet HTML)
  const capitolato = (() => {
    const parts: string[] = ['Realizzazione di controsoffitto a secco costituito da:'];
    if (isSuspended) {
      parts.push(`- struttura sospesa al solaio mediante pendini regolabili a passo ${ceiling.hangerSpacingPrimaryMm}×${ceiling.hangerSpacingPerpendicularMm}mm, con camera tecnica di altezza ${ceiling.ceilingDropMm}mm;`);
    } else {
      parts.push('- struttura fissata direttamente al solaio (sistema aderente);');
    }
    if (primaryFrames.length > 0) {
      const pf = primaryFrames[0].material;
      parts.push(`- profili portanti primari a C in acciaio zincato larghezza ${pf?.width ?? '—'}mm, posati ad interasse ${ceiling.primarySpacingMm}mm;`);
    }
    if (secondaryFrames.length > 0) {
      const sf = secondaryFrames[0].material;
      parts.push(`- profili portanti secondari a C larghezza ${sf?.width ?? '—'}mm, orditi perpendicolarmente ai primari ad interasse ${ceiling.secondarySpacingMm ?? 500}mm;`);
    }
    if (insulations.length > 0) {
      const ins = insulations[0].material;
      const t = insulations[0].thickness ?? ins?.thickness ?? 0;
      parts.push(`- materassino isolante in ${ins?.material_type ?? 'fibra minerale'} spessore ${t}mm, posato sull'orditura;`);
    }
    if (boards.length > 0) {
      const firstBoard = boards[0].material;
      parts.push(`- rivestimento a vista in n.${boards.length} lastre di ${firstBoard?.material_type ?? 'cartongesso'} spessore ${firstBoard?.thickness ?? '12,5'}mm;`);
    }
    if (screwsLayers.length > 0) {
      parts.push('- fissaggio delle lastre con viti autofilettanti per cartongesso, interasse conforme a UNI 11424:2015.');
    }
    parts.push(`Lo spessore complessivo del pacchetto sospeso è di ${totalThickness}mm.`);
    return parts.join(' ');
  })();

  const capLines = doc.splitTextToSize(capitolato, pageW - margin * 2);
  capLines.forEach((line: string) => {
    if (cursorY > pageH - 15) { doc.addPage(); cursorY = margin; }
    doc.text(line, margin, cursorY);
    cursorY += 3.8;
  });
  cursorY += 4;

  // ===== NORME =====
  if (cursorY > pageH - 50) { doc.addPage(); cursorY = margin; }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Norme di riferimento', margin, cursorY);
  cursorY += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const norms = [
    'UNI 11424:2015 — Sistemi a secco con lastre di gesso rivestito',
    'UNI EN 520 — Lastre di gesso rivestito (tipi e proprietà)',
    'UNI EN 13964 — Controsoffitti: requisiti e metodi di prova',
    'UNI EN 13501-1 — Classificazione di reazione al fuoco',
    'UNI EN ISO 717-1 — Valutazione isolamento acustico',
    'UNI EN 14195 — Profili metallici per sistemi a secco',
  ];
  norms.forEach(n => {
    if (cursorY > pageH - 15) { doc.addPage(); cursorY = margin; }
    doc.text(`· ${n}`, margin, cursorY);
    cursorY += 3.8;
  });

  // ===== FOOTER =====
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.setFont('helvetica', 'normal');
    const dt = new Date().toLocaleDateString('it-IT');
    doc.text(`Generato da DryConfig · ${dt}`, margin, pageH - 8);
    doc.text(`Pag. ${p}/${totalPages}`, pageW - margin, pageH - 8, { align: 'right' });
  }

  // Save
  const safeName = (opts.stratigraphyName || opts.systemCode || 'controsoffitto')
    .replace(/[^a-z0-9-_ ]/gi, '_')
    .trim() || 'controsoffitto';
  doc.save(`${safeName}.pdf`);
}
